import { deleteAsync, getAsync, postAsync, putAsync } from '../../../../shared/api.service'
import { queryString } from '../../../../shared/sso-workspace/auth-api'

// The Packages staff APIs (rapid-api-packages' /api/packages/admin/...),
// called straight at REACT_APP_API_PACKAGES with the staff session (never
// through the gateway). Like the ai-tools module: plain functions, each
// resolving to the response body and, on failure, throwing the response
// ({ status, data: { code, message } }), so serverMessage(err) reads the
// server's sentence and <ErrorReference error={err} /> its request id. Being
// module functions they keep one identity for the life of the app, unlike
// api-new's hooks, so they are safe in any effect's or memo's deps.
//
// Verbs on SYS_PACKAGES: read = every GET; create = add a package; update =
// deprecate and create or rotate a key; delete = delete a version and revoke
// a key. Channels are 'unity' (released) and 'unity-dev' (pre-release).
const base = () => process.env.REACT_APP_API_PACKAGES

// Whether this build knows where the packages service is (prod before its
// cutover does not: the section is hidden).
export const packagesAvailable = () => Boolean(base())

const unwrap = async call => {
  try {
    const result = await call()
    return result ? result.data : undefined
  } catch (err) {
    throw (err && err.response) || err
  }
}

const seg = value => encodeURIComponent(String(value))
const adminPath = (...parts) => ['api/packages/admin', ...parts.map(seg)].join('/')
const versionPath = (name, channel, version) => `${adminPath(name)}/versions/${seg(channel)}/${seg(version)}`

// [PackageSummary]
export const listPackages = () => unwrap(() => getAsync(base(), adminPath()))
// 201 PackageSummary (empty channels)
export const addPackage = ({ name, displayName }) => unwrap(() => postAsync(base(), adminPath(), { name, displayName }))
// [VersionView], highest semver first; channel is required, deleted: true adds the deleted ones
export const listVersions = (name, channel, { deleted } = {}) =>
  unwrap(() => getAsync(base(), `${adminPath(name)}/versions${queryString({ channel, deleted: deleted ? 1 : undefined })}`))
// VersionDetail (manifest, changelog, downloads by day)
export const getVersion = (name, channel, version) => unwrap(() => getAsync(base(), versionPath(name, channel, version)))
// { url, expiresAt } (10 minutes); 404 for a deleted version
export const getDownloadURL = (name, channel, version) => unwrap(() => getAsync(base(), `${versionPath(name, channel, version)}/download-url`))
// message: 1..1000 characters to deprecate, null to undeprecate -> VersionView
export const setDeprecated = (name, channel, version, message) =>
  unwrap(() => putAsync(base(), versionPath(name, channel, version), { deprecated: message }))
// confirm must equal the version; reason at most 500 characters -> 204
export const deleteVersion = (name, channel, version, { confirm, reason } = {}) =>
  unwrap(() => deleteAsync(base(), `${versionPath(name, channel, version)}${queryString({ confirm, reason })}`))

// [credentials.KeyView]
export const listKeys = name => unwrap(() => getAsync(base(), `${adminPath(name)}/keys`))
// 201 credentials.Response (snake_case; `secret` is the key secret, shown once; `key` is the KeyView)
export const createKey = (name, { name: keyName, description, expires_at }) =>
  unwrap(() => postAsync(base(), `${adminPath(name)}/keys`, { name: keyName, description, expires_at }))
export const rotateKey = (name, keyID, { grace_hours, expires_at }) =>
  unwrap(() => postAsync(base(), `${adminPath(name)}/keys/${seg(keyID)}/rotate`, { grace_hours, expires_at }))
export const revokeKey = (name, keyID, { reason } = {}) =>
  unwrap(() => deleteAsync(base(), `${adminPath(name)}/keys/${seg(keyID)}${queryString({ reason })}`))
